import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { Student, ChalkboardTeacher, UsersThree, IdentificationBadge, ArrowRight } from "@phosphor-icons/react";

const roles = [
  { id: "admin", name: "Administrator", desc: "Registrar / Principal / VC. Full control of every module.", icon: IdentificationBadge },
  { id: "faculty", name: "Faculty", desc: "Take attendance, run exams, manage course content & grades.", icon: ChalkboardTeacher },
  { id: "student", name: "Student", desc: "View courses, assignments, fees, attendance, results.", icon: Student },
  { id: "parent", name: "Parent / Guardian", desc: "Live updates on attendance, fees, exams, bus.", icon: UsersThree },
];

export default function SelectRole() {
  const { user, setRole, loading } = useAuth();
  const nav = useNavigate();
  const [role, setRoleLocal] = useState("admin");
  const [inst, setInst] = useState("higher_ed");
  const [busy, setBusy] = useState(false);

  // Side-effect redirects belong in useEffect, not during render — fixes the
  // race where AuthCallback's setUser hasn't flushed yet and we bounce to /login.
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    if (user.role) { nav("/app", { replace: true }); }
  }, [user, loading, nav]);

  if (loading || !user || user.role) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
    );
  }

  const proceed = async () => {
    setBusy(true);
    try { await setRole(role, inst); nav("/app", { replace: true }); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="px-6 lg:px-10 h-16 flex items-center justify-between border-b border-border">
        <Logo/>
        <div className="text-xs text-muted-foreground">Hello, <span className="text-foreground font-medium">{user.name}</span></div>
      </div>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <div className="eos-overline">Set up workspace</div>
        <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight mt-2">How will you use EduOS?</h1>
        <p className="text-muted-foreground mt-3 max-w-xl">Pick a primary role. You can switch later — every account stays tied to one canonical record.</p>

        {/* Institution toggle */}
        <div className="mt-10">
          <div className="eos-overline mb-3">Institution type</div>
          <div className="inline-flex border border-border rounded-md overflow-hidden" data-testid="institution-toggle">
            {[
              { v: "higher_ed", label: "Higher Education" },
              { v: "k12", label: "K-12 School" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setInst(o.v)}
                className={`px-5 py-2.5 text-sm font-medium ${inst === o.v ? "bg-foreground text-background" : "bg-white text-foreground hover:bg-secondary"}`}
                data-testid={`institution-${o.v}`}
              >{o.label}</button>
            ))}
          </div>
        </div>

        {/* Roles */}
        <div className="mt-10 grid md:grid-cols-2 gap-px bg-border border border-border rounded-md overflow-hidden">
          {roles.map((r) => {
            const active = role === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setRoleLocal(r.id)}
                className={`text-left p-6 transition-colors ${active ? "bg-[hsl(244_53%_20%)] text-white" : "bg-white hover:bg-secondary"}`}
                data-testid={`role-${r.id}`}
              >
                <r.icon size={26} weight="duotone" className={active ? "text-[hsl(20_90%_55%)]" : "text-[hsl(244_53%_20%)]"}/>
                <div className="font-heading font-semibold text-lg tracking-tight mt-4">{r.name}</div>
                <div className={`text-sm mt-1 ${active ? "text-white/70" : "text-muted-foreground"}`}>{r.desc}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex justify-end">
          <Button onClick={proceed} disabled={busy} className="h-11 px-6 rounded-md bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="onboarding-continue-btn">
            {busy ? "Setting up…" : "Open workspace"} <ArrowRight size={16} weight="bold" className="ml-2"/>
          </Button>
        </div>
      </div>
    </div>
  );
}
